import * as React from "react";
import { CheckIcon, ChevronDownIcon } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export const TRADE_FILTER_TEXT_CLASS =
  "text-gray-500 dark:text-sidebar-muted-foreground";

/** Filter-bar select triggers — muted label in every interaction state (closed, hover, open). */
export const TRADE_FILTER_SELECT_TRIGGER_CLASS = cn(
  TRADE_FILTER_TEXT_CLASS,
  "hover:!text-gray-500 dark:hover:!text-sidebar-muted-foreground",
  "data-[state=open]:!text-gray-500 dark:data-[state=open]:!text-sidebar-muted-foreground",
  "focus-visible:!text-gray-500 dark:focus-visible:!text-sidebar-muted-foreground",
  "[&_[data-slot=select-value]]:!text-gray-500 dark:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground",
  "hover:[&_[data-slot=select-value]]:!text-gray-500 dark:hover:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground",
  "data-[state=open]:[&_[data-slot=select-value]]:!text-gray-500 dark:data-[state=open]:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground",
  "focus-visible:[&_[data-slot=select-value]]:!text-gray-500 dark:focus-visible:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground",
  "[&_svg]:!text-gray-500 dark:[&_svg]:!text-sidebar-muted-foreground",
  "hover:[&_svg]:!text-gray-500 dark:hover:[&_svg]:!text-sidebar-muted-foreground",
  "data-[state=open]:[&_svg]:!text-gray-500 dark:data-[state=open]:[&_svg]:!text-sidebar-muted-foreground",
  "focus-visible:[&_svg]:!text-gray-500 dark:focus-visible:[&_svg]:!text-sidebar-muted-foreground",
);

/** Portaled Trade Log filter menu — inherit grey labels (not popover-foreground). */
export const TRADE_LOG_FILTER_SELECT_CONTENT_CLASS =
  "text-gray-500 dark:text-sidebar-muted-foreground";

/** Trade Log filter rows — label stays grey when highlighted/focused (ItemText is a child span). */
export const TRADE_LOG_FILTER_SELECT_ITEM_CLASS = cn(
  TRADE_FILTER_TEXT_CLASS,
  "data-highlighted:!text-gray-500 focus:!text-gray-500 data-[state=checked]:!text-gray-500",
  "data-highlighted:**:!text-gray-500 focus:**:!text-gray-500 data-[state=checked]:**:!text-gray-500",
  "dark:data-highlighted:!text-sidebar-muted-foreground dark:focus:!text-sidebar-muted-foreground dark:data-[state=checked]:!text-sidebar-muted-foreground",
  "dark:data-highlighted:**:!text-sidebar-muted-foreground dark:focus:**:!text-sidebar-muted-foreground dark:data-[state=checked]:**:!text-sidebar-muted-foreground",
);

/** Full-width field select — matches trade details journal mood selects (not filter-bar popper style). */
export const FIELD_SELECT_TRIGGER_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-sidebar-muted-foreground w-full [&_[data-slot=select-value]]:text-gray-500 dark:[&_[data-slot=select-value]]:text-sidebar-muted-foreground";

/** Trade Log / simulator filter text inputs — page canvas fill in dark mode (distinct from sidebar cards). */
export const TRADE_LOG_FILTER_INPUT_CLASS = cn(
  TRADE_FILTER_TEXT_CLASS,
  "font-[family-name:var(--font-primary)] text-sm font-normal transition-none",
  "!border-border !bg-content-canvas dark:!bg-content-canvas",
  "placeholder:!text-gray-500 dark:placeholder:!text-sidebar-muted-foreground",
  "focus-visible:!border-ring focus-visible:!ring-3 focus-visible:!ring-ring/50",
);

/** Trade Log toolbar selects — sidebar fill in dark mode (overrides global select-trigger input/30). */
export const TRADE_LOG_FILTER_SELECT_TRIGGER_CLASS = cn(
  FIELD_SELECT_TRIGGER_CLASS,
  "transition-none",
  "!bg-content-canvas hover:!bg-content-canvas data-[state=open]:!bg-content-canvas focus-visible:!bg-content-canvas",
  "dark:!bg-content-canvas dark:data-[state=closed]:!bg-content-canvas dark:hover:!bg-content-canvas dark:data-[state=open]:!bg-content-canvas dark:focus-visible:!bg-content-canvas",
  "hover:!text-gray-500 data-[state=open]:!text-gray-500 focus-visible:!text-gray-500",
  "hover:[&_[data-slot=select-value]]:!text-gray-500 data-[state=open]:[&_[data-slot=select-value]]:!text-gray-500 focus-visible:[&_[data-slot=select-value]]:!text-gray-500",
  "hover:[&_svg]:!text-gray-500 data-[state=open]:[&_svg]:!text-gray-500 focus-visible:[&_svg]:!text-gray-500",
  "dark:hover:!text-sidebar-muted-foreground dark:data-[state=open]:!text-sidebar-muted-foreground dark:focus-visible:!text-sidebar-muted-foreground",
  "dark:hover:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground dark:data-[state=open]:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground dark:focus-visible:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground",
  "dark:hover:[&_svg]:!text-sidebar-muted-foreground dark:data-[state=open]:[&_svg]:!text-sidebar-muted-foreground dark:focus-visible:[&_svg]:!text-sidebar-muted-foreground",
);

/** Simulator list card — same trigger chrome as Trade Log (content canvas on sidebar card). */
export const SIMULATOR_LIST_FILTER_SELECT_TRIGGER_CLASS = TRADE_LOG_FILTER_SELECT_TRIGGER_CLASS;

/** Trade Log Columns dropdown — field-select trigger chrome (checkbox menu, not Radix Select). */
export const TRADE_LOG_COLUMNS_SELECT_TRIGGER_CLASS = cn(
  FIELD_SELECT_TRIGGER_CLASS,
  "inline-flex h-8 w-fit min-w-0 items-center justify-between gap-1.5 rounded-[6px] border border-border py-2 pr-2 pl-2.5 transition-none",
  "!bg-content-canvas hover:!bg-content-canvas data-[state=open]:!bg-content-canvas focus-visible:!bg-content-canvas",
  "dark:!bg-content-canvas dark:data-[state=closed]:!bg-content-canvas dark:hover:!bg-content-canvas dark:data-[state=open]:!bg-content-canvas dark:focus-visible:!bg-content-canvas",
  "hover:!text-gray-500 data-[state=open]:!text-gray-500 focus-visible:!text-gray-500",
  "hover:[&_[data-slot=select-value]]:!text-gray-500 data-[state=open]:[&_[data-slot=select-value]]:!text-gray-500 focus-visible:[&_[data-slot=select-value]]:!text-gray-500",
  "hover:[&_svg]:!text-gray-500 data-[state=open]:[&_svg]:!text-gray-500 focus-visible:[&_svg]:!text-gray-500",
  "dark:hover:!text-sidebar-muted-foreground dark:data-[state=open]:!text-sidebar-muted-foreground dark:focus-visible:!text-sidebar-muted-foreground",
  "dark:hover:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground dark:data-[state=open]:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground dark:focus-visible:[&_[data-slot=select-value]]:!text-sidebar-muted-foreground",
  "dark:hover:[&_svg]:!text-sidebar-muted-foreground dark:data-[state=open]:[&_svg]:!text-sidebar-muted-foreground dark:focus-visible:[&_svg]:!text-sidebar-muted-foreground",
);

/** Trade Log Columns menu rows — sidebar accent hover in dark mode. */
export const TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS =
  "rounded-[4px] capitalize dark:text-sidebar-muted-foreground dark:data-highlighted:text-sidebar-muted-foreground dark:focus:text-sidebar-muted-foreground dark:data-highlighted:bg-sidebar-accent dark:focus:bg-sidebar-accent";

/** Dashboard card selects — match parent card surface (not Trade Log sidebar panel). */
export const CARD_FILTER_SELECT_TRIGGER_CLASS = cn(
  FIELD_SELECT_TRIGGER_CLASS,
  "!bg-card data-[state=closed]:!bg-card hover:!bg-card focus-visible:!bg-card",
  "transition-none dark:!bg-card dark:data-[state=closed]:!bg-card dark:hover:!bg-card dark:focus-visible:!bg-card",
  "dark:hover:!text-muted-foreground dark:data-[state=open]:!text-muted-foreground",
  "dark:hover:[&_[data-slot=select-value]]:!text-muted-foreground dark:data-[state=open]:[&_[data-slot=select-value]]:!text-muted-foreground",
  "dark:hover:[&_svg]:!text-muted-foreground dark:data-[state=open]:[&_svg]:!text-muted-foreground",
);

export type TradeFilterSelectChrome = false | "sidebar" | "card" | "reports" | "simulator-list";

/** Radix Select item values cannot be empty; maps to/from configurator `""` (any). */
export const TRADE_FILTER_EMPTY_VALUE = "__filter_empty__";

/** Fixed filter column width — mirrored in globals.css `[data-trade-filter-bar]` */
export const TRADE_FILTER_FIELD_WIDTH_CLASS =
  "w-[10rem] min-w-[10rem] max-w-[10rem] shrink-0 basis-[10rem]";

export function TradeFilterField({ children }: { children: React.ReactNode }) {
  return <div className={TRADE_FILTER_FIELD_WIDTH_CLASS}>{children}</div>;
}

export interface TradeFilterSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Extra searchable text (not shown in the trigger). */
  keywords?: string;
  /** Shown under the option label. Interaction copy explains which level fires. */
  description?: string;
}

/** Optional column groups for multi-column pickers (e.g. Strategy R&D setups). */
export type TradeFilterSelectOptionGroup = {
  id: string;
  label: string;
  options: TradeFilterSelectOption[];
};

export interface TradeFilterSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: TradeFilterSelectOption[];
  disabled?: boolean;
  placeholder?: string;
  /** Associates the trigger with a FieldLabel `htmlFor`. */
  id?: string;
  /** When true and value is "", show placeholder instead of selecting empty sentinel. */
  emptyValueShowsPlaceholder?: boolean;
  /** `filter` = 10rem column (Trade Log / Reports / Statistics bars). `full` = 100% width. `fit` = content width. */
  layout?: "filter" | "full" | "fit";
  /** With `layout="fit"`, size the trigger to the widest option label (and placeholder when set). */
  fitToLongestOption?: boolean;
}

/** Padding inside select trigger beyond label text (pl + pr + chevron + gaps). */
const FIT_SELECT_TRIGGER_CHROME_PX = 52;
/** Extra horizontal chrome on dropdown items (pl + pr-8 check indicator gutter). */
const FIT_SELECT_ITEM_CHROME_PX = 52;

function estimateFitSelectWidth(labels: string[]): number | undefined {
  if (labels.length === 0) return undefined;
  const longest = labels.reduce((current, label) =>
    label.length > current.length ? label : current
  );
  // 0.875rem text — ~7.6px per average char before DOM measure completes.
  return Math.ceil(longest.length * 7.6) + FIT_SELECT_ITEM_CHROME_PX;
}

function useFitSelectTriggerWidth(
  labels: string[],
  enabled: boolean
): { measurer: React.ReactNode; width: number | undefined } {
  const measureRef = React.useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = React.useState<number | undefined>();
  const labelKey = labels.join("\0");
  const estimatedWidth = React.useMemo(
    () => (enabled ? estimateFitSelectWidth(labels) : undefined),
    [enabled, labelKey, labels]
  );

  React.useLayoutEffect(() => {
    if (!enabled) {
      setMeasuredWidth(undefined);
      return;
    }

    const measure = () => {
      const container = measureRef.current;
      if (!container) return;

      let max = 0;
      container.querySelectorAll("[data-fit-select-measure]").forEach((node) => {
        const el = node as HTMLElement;
        max = Math.max(max, el.scrollWidth, el.offsetWidth, el.getBoundingClientRect().width);
      });
      const chromePx = Math.max(FIT_SELECT_TRIGGER_CHROME_PX, FIT_SELECT_ITEM_CHROME_PX);
      const next = max > 0 ? Math.ceil(max) + chromePx : undefined;
      setMeasuredWidth((previous) => (previous === next ? previous : next));
    };

    measure();
    void document.fonts?.ready.then(measure);
  }, [enabled, labelKey]);

  const measurer = enabled ? (
    <div
      ref={measureRef}
      aria-hidden
      className="pointer-events-none absolute top-0 left-0 -z-50 w-max"
      style={{ visibility: "hidden" }}
    >
      {labels.map((label, index) => (
        <span
          key={`${label}-${index}`}
          data-fit-select-measure
          className={cn(
            TRADE_FILTER_TEXT_CLASS,
            "inline-block whitespace-nowrap text-sm font-normal font-[family-name:var(--font-primary)]"
          )}
        >
          {label}
        </span>
      ))}
    </div>
  ) : null;

  const width = measuredWidth ?? estimatedWidth;

  return { measurer, width };
}

function fitSelectWidthStyle(
  fitWidth: number | undefined
): React.CSSProperties | undefined {
  if (!fitWidth) return undefined;
  return {
    "--fit-select-width": `${fitWidth}px`,
    width: fitWidth,
    minWidth: fitWidth,
    maxWidth: `min(calc(100vw - 2rem), ${fitWidth}px)`,
  } as React.CSSProperties;
}

function toRadixSelectValue(value: string): string {
  return value === "" ? TRADE_FILTER_EMPTY_VALUE : value;
}

function fromRadixSelectValue(value: string): string {
  return value === TRADE_FILTER_EMPTY_VALUE ? "" : value;
}

function FieldSelectItems({
  options,
  itemClassName,
}: {
  options: TradeFilterSelectOption[];
  itemClassName?: string;
}): React.ReactElement {
  return (
    <SelectGroup>
      {options.map((option, index) => (
        <SelectItem
          key={`${toRadixSelectValue(option.value)}-${index}`}
          value={toRadixSelectValue(option.value)}
          disabled={option.disabled}
          className={cn(
            TRADE_LOG_FILTER_SELECT_ITEM_CLASS,
            "font-[family-name:var(--font-primary)] font-normal",
            "dark:data-highlighted:!bg-sidebar-accent dark:focus:!bg-sidebar-accent dark:data-[state=checked]:!bg-sidebar-accent",
            "data-[state=checked]:bg-[color-mix(in_oklab,var(--brand-tropical-surf)_14%,transparent)]",
            "data-[state=checked]:text-foreground",
            "data-[state=checked]:[&_svg]:text-[color:var(--brand-tropical-surf)]",
            itemClassName,
            option.disabled && "cursor-not-allowed opacity-45"
          )}
        >
          <span className="flex min-w-0 flex-col">
            <span>{option.label}</span>
            {option.description ? (
              <span className="mt-0.5 block max-w-[22rem] whitespace-normal text-[11px] font-normal leading-snug text-muted-foreground">
                {option.description}
              </span>
            ) : null}
          </span>
        </SelectItem>
      ))}
    </SelectGroup>
  );
}

function FieldSelectGroupedItems({
  groups,
}: {
  groups: TradeFilterSelectOptionGroup[];
}): React.ReactElement {
  return (
    <div
      data-field-select-columns=""
      className="grid w-full grid-cols-3 gap-x-2 gap-y-3 p-1"
    >
      {groups.map((group) => (
        <SelectGroup key={group.id} className="min-w-0">
          <SelectLabel
            className={cn(
              "px-1.5 py-1.5 font-[family-name:var(--font-primary)] text-[10px] font-semibold uppercase tracking-[0.06em]",
              "text-gray-400 dark:text-sidebar-muted-foreground"
            )}
          >
            {group.label}
          </SelectLabel>
          {group.options.map((option, index) => (
            <SelectItem
              key={`${group.id}-${toRadixSelectValue(option.value)}-${index}`}
              value={toRadixSelectValue(option.value)}
              disabled={option.disabled}
              className={cn(
                TRADE_LOG_FILTER_SELECT_ITEM_CLASS,
                "font-[family-name:var(--font-primary)] font-normal",
                "dark:data-highlighted:!bg-sidebar-accent dark:focus:!bg-sidebar-accent dark:data-[state=checked]:!bg-sidebar-accent",
                "data-[state=checked]:bg-[color-mix(in_oklab,var(--brand-tropical-surf)_14%,transparent)]",
                "data-[state=checked]:text-foreground",
                "data-[state=checked]:[&_svg]:text-[color:var(--brand-tropical-surf)]",
                option.disabled && "cursor-not-allowed opacity-45"
              )}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      ))}
    </div>
  );
}

export function TradeFilterSelect({
  value,
  onValueChange,
  options,
  disabled = false,
  placeholder,
  id,
  emptyValueShowsPlaceholder = false,
  layout = "filter",
  fitToLongestOption = false,
}: TradeFilterSelectProps) {
  const selectValue =
    emptyValueShowsPlaceholder && value === "" ? undefined : toRadixSelectValue(value);

  const measureLabels = React.useMemo(() => {
    const labels = options.map((option) => option.label);
    if (placeholder) labels.push(placeholder);
    return labels;
  }, [options, placeholder]);

  const { measurer, width: fitWidth } = useFitSelectTriggerWidth(
    measureLabels,
    layout === "fit" && fitToLongestOption
  );

  const fitDropdown = layout === "fit" && fitToLongestOption;

  const select = (
    <Select
      value={selectValue}
      onValueChange={(next) => onValueChange(fromRadixSelectValue(next))}
      disabled={disabled}
    >
      <SelectTrigger
        id={id}
        size="default"
        data-fit-select-measured={fitWidth ? "" : undefined}
        className={cn(
          TRADE_FILTER_SELECT_TRIGGER_CLASS,
          layout === "full" && "w-full"
        )}
        style={layout === "fit" && fitToLongestOption ? fitSelectWidthStyle(fitWidth) : undefined}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        position="popper"
        fitToContent={fitDropdown}
        data-fit-select-dropdown={fitDropdown ? "" : undefined}
        data-trade-log-filter-select-content=""
        {...(fitDropdown
          ? {
              side: "bottom" as const,
              sideOffset: 4,
              avoidCollisions: false,
            }
          : {})}
        className={(layout === "full" || layout === "fit") ? "z-[200]" : undefined}
        style={fitDropdown ? fitSelectWidthStyle(fitWidth) : undefined}
      >
        <SelectGroup>
          {options.map((option, index) => (
            <SelectItem
              key={`${toRadixSelectValue(option.value)}-${index}`}
              value={toRadixSelectValue(option.value)}
              disabled={option.disabled}
              className={cn(
                TRADE_LOG_FILTER_SELECT_ITEM_CLASS,
                "font-[family-name:var(--font-primary)] font-normal",
                fitDropdown && "w-max max-w-none whitespace-nowrap",
                option.disabled && "opacity-45 cursor-not-allowed"
              )}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );

  if (layout === "full") {
    return select;
  }

  if (layout === "fit") {
    return (
      <div
        className="relative w-fit"
        data-fit-select=""
        data-fit-select-sized={fitWidth ? "" : undefined}
        style={fitWidth ? fitSelectWidthStyle(fitWidth) : undefined}
      >
        {measurer}
        {select}
      </div>
    );
  }

  return <TradeFilterField>{select}</TradeFilterField>;
}

/** Full-width select inside policy rule / simulator field rows (`data-trade-filter-bar-layout="full"`). */
export function TradeFilterSelectFullWidth(props: Omit<TradeFilterSelectProps, "layout">): React.ReactElement {
  return (
    <div data-trade-filter-bar="" data-trade-filter-bar-layout="full" className="w-full min-w-0">
      <TradeFilterSelect {...props} layout="full" />
    </div>
  );
}

/** Full-width select with trade-details journal open/close behavior (account settings, forms). */
export function FieldSelectFullWidth({
  value,
  onValueChange,
  options,
  optionGroups,
  disabled = false,
  placeholder,
  id,
  emptyValueShowsPlaceholder = false,
  /** Size trigger to the longest option label (Strategy R&D dense rows). */
  fitToLongestOption = false,
  /** Overrides the trigger native tooltip (defaults to selected label). */
  title,
}: Omit<TradeFilterSelectProps, "layout"> & {
  /** When set, renders a multi-column dropdown with group headings. */
  optionGroups?: TradeFilterSelectOptionGroup[];
  title?: string;
}): React.ReactElement {
  const flatOptions = React.useMemo(() => {
    if (optionGroups?.length) {
      return optionGroups.flatMap((g) => g.options);
    }
    return options;
  }, [optionGroups, options]);

  const selectValue =
    emptyValueShowsPlaceholder && value === "" ? undefined : toRadixSelectValue(value);
  const selectedLabel = React.useMemo(() => {
    const match = flatOptions.find((option) => option.value === value);
    if (match?.label?.trim()) return match.label;
    const trimmed = (value || "").trim();
    return trimmed || undefined;
  }, [flatOptions, value]);

  const measureLabels = React.useMemo(() => {
    const labels = flatOptions.map((option) => option.label);
    if (placeholder) labels.push(placeholder);
    if (selectedLabel) labels.push(selectedLabel);
    return labels;
  }, [flatOptions, placeholder, selectedLabel]);

  const { measurer, width: fitWidth } = useFitSelectTriggerWidth(
    measureLabels,
    fitToLongestOption
  );

  const grouped = Boolean(optionGroups && optionGroups.length > 0);
  const fitDropdown = fitToLongestOption && !grouped;

  return (
    <div
      data-field-select=""
      data-fit-select={fitToLongestOption ? "" : undefined}
      data-fit-select-sized={fitToLongestOption && fitWidth ? "" : undefined}
      className={cn(
        "relative min-w-0",
        fitToLongestOption ? "w-fit max-w-full" : "w-full"
      )}
      style={fitToLongestOption ? fitSelectWidthStyle(fitWidth) : undefined}
    >
      {measurer}
      <Select
        value={selectValue}
        onValueChange={(next) => onValueChange(fromRadixSelectValue(next))}
        disabled={disabled}
        className={fitToLongestOption ? "w-full" : "w-full min-w-0"}
      >
        <SelectTrigger
          id={id}
          data-fit-select-measured={fitToLongestOption && fitWidth ? "" : undefined}
          className={cn(
            "w-full",
            !fitToLongestOption && "min-w-0",
            FIELD_SELECT_TRIGGER_CLASS,
            !fitToLongestOption &&
              "[&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:flex-1 [&_[data-slot=select-value]]:truncate",
            fitToLongestOption &&
              "[&_[data-slot=select-value]]:whitespace-nowrap [&_[data-slot=select-value]]:overflow-visible"
          )}
          style={fitToLongestOption ? fitSelectWidthStyle(fitWidth) : undefined}
          title={title?.trim() || selectedLabel}
        >
          <SelectValue placeholder={placeholder}>{selectedLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent
          position="popper"
          align="start"
          fitToContent={fitDropdown || !grouped}
          side="bottom"
          sideOffset={4}
          avoidCollisions={false}
          className={cn(
            "z-[200]",
            !grouped &&
              !fitDropdown &&
              "w-(--radix-select-trigger-width) min-w-(--radix-select-trigger-width) max-w-(--radix-select-trigger-width)"
          )}
          data-strategy-rd-grouped-select={grouped ? "" : undefined}
          data-fit-select-dropdown={fitDropdown ? "" : undefined}
          style={fitDropdown ? fitSelectWidthStyle(fitWidth) : undefined}
        >
          {grouped && optionGroups ? (
            <FieldSelectGroupedItems groups={optionGroups} />
          ) : (
            <FieldSelectItems
              options={options}
              itemClassName={fitDropdown ? "w-max max-w-none whitespace-nowrap" : undefined}
            />
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

export type FieldMultiSelectFullWidthProps = {
  id?: string;
  options: TradeFilterSelectOption[];
  optionGroups?: Array<{
    id: string;
    label: string;
    options: TradeFilterSelectOption[];
  }>;
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
};

/**
 * Full-width multi-select matching FieldSelectFullWidth trigger styling
 * (same font weight / size / muted text as Setup / Entry on Strategy R&D).
 */
export function FieldMultiSelectFullWidth({
  id,
  options,
  optionGroups,
  selected,
  onChange,
  placeholder = "Select…",
  disabled = false,
}: FieldMultiSelectFullWidthProps): React.ReactElement {
  const flatOptions = React.useMemo(
    () =>
      optionGroups?.length
        ? optionGroups.flatMap((group) => group.options)
        : options,
    [optionGroups, options]
  );
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);
  const labelByValue = React.useMemo(
    () => new Map(flatOptions.map((o) => [o.value, o.label] as const)),
    [flatOptions]
  );
  const triggerLabel = React.useMemo(() => {
    if (selected.length === 0) return placeholder;
    return selected.map((v) => labelByValue.get(v) ?? v).join(", ");
  }, [labelByValue, placeholder, selected]);

  return (
    <div data-field-select="" className="w-full min-w-0">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            id={id}
            role="combobox"
            data-slot="select-trigger"
            data-size="default"
            disabled={disabled}
            title={triggerLabel}
            className={cn(
              "flex h-8 w-full min-w-0 items-center justify-between gap-1.5 rounded-[6px] border border-input bg-transparent py-2 pr-2 pl-2.5 text-sm whitespace-nowrap outline-none select-none transition-colors",
              "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "dark:bg-input/30 dark:hover:bg-input/50",
              FIELD_SELECT_TRIGGER_CLASS
            )}
          >
            <span
              data-slot="select-value"
              className={cn(
                "min-w-0 flex-1 truncate text-left",
                selected.length === 0 && "text-muted-foreground"
              )}
            >
              {triggerLabel}
            </span>
            <ChevronDownIcon
              className="size-4 shrink-0 opacity-50 dark:opacity-100 dark:text-sidebar-muted-foreground"
              aria-hidden
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          side="bottom"
          sideOffset={4}
          avoidCollisions={false}
          className={cn(
            "z-[200] max-h-56 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto p-1",
            TRADE_LOG_FILTER_SELECT_CONTENT_CLASS
          )}
          data-field-select-menu=""
        >
          {optionGroups?.length
            ? optionGroups.map((group) => (
                <div key={group.id} className="flex flex-col">
                  {group.id !== "all" ? (
                    <p className="px-2 py-1 font-[family-name:var(--font-primary)] text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                      {group.label}
                    </p>
                  ) : null}
                  {group.options.map((option) => (
                    <DropdownMenuCheckboxItem
                      key={option.value}
                      checked={selectedSet.has(option.value)}
                      disabled={option.disabled}
                      onCheckedChange={() => {
                        if (option.disabled) return;
                        if (selectedSet.has(option.value)) {
                          onChange(selected.filter((v) => v !== option.value));
                        } else {
                          onChange([...selected, option.value]);
                        }
                      }}
                      onSelect={(event) => event.preventDefault()}
                      className={MULTI_FILTER_CHECKBOX_ITEM_CLASS}
                    >
                      <span className="min-w-0 truncate">{option.label}</span>
                    </DropdownMenuCheckboxItem>
                  ))}
                </div>
              ))
            : options.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={selectedSet.has(option.value)}
              disabled={option.disabled}
              onCheckedChange={() => {
                if (option.disabled) return;
                if (selectedSet.has(option.value)) {
                  onChange(selected.filter((v) => v !== option.value));
                } else {
                  onChange([...selected, option.value]);
                }
              }}
              onSelect={(event) => event.preventDefault()}
              className={MULTI_FILTER_CHECKBOX_ITEM_CLASS}
            >
              <span className="min-w-0 truncate">{option.label}</span>
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** Fit-width field select — journal open/close style, sized to the longest option label. */
export function FieldSelectFit({
  value,
  onValueChange,
  options,
  disabled = false,
  placeholder,
  id,
  emptyValueShowsPlaceholder = false,
  fitToLongestOption = true,
  /** Trade Log toolbar chrome, or card surface for dashboard widgets. */
  filterChrome = false,
}: Omit<TradeFilterSelectProps, "layout"> & { filterChrome?: TradeFilterSelectChrome }): React.ReactElement {
  const selectValue =
    emptyValueShowsPlaceholder && value === "" ? undefined : toRadixSelectValue(value);

  const measureLabels = React.useMemo(() => {
    const labels = options.map((option) => option.label);
    if (placeholder) labels.push(placeholder);
    return labels;
  }, [options, placeholder]);

  const { measurer, width: fitWidth } = useFitSelectTriggerWidth(
    measureLabels,
    fitToLongestOption
  );

  const filterTriggerClass =
    filterChrome === "sidebar" || filterChrome === "reports"
      ? TRADE_LOG_FILTER_SELECT_TRIGGER_CLASS
      : filterChrome === "card"
        ? CARD_FILTER_SELECT_TRIGGER_CLASS
        : FIELD_SELECT_TRIGGER_CLASS;

  return (
    <div
      data-field-select=""
      data-fit-select=""
      data-fit-select-sized={fitWidth ? "" : undefined}
      className="relative w-fit min-w-0"
      style={fitToLongestOption ? fitSelectWidthStyle(fitWidth) : undefined}
    >
      {measurer}
      <Select
        value={selectValue}
        onValueChange={(next) => onValueChange(fromRadixSelectValue(next))}
        disabled={disabled}
        className="w-full"
      >
        <SelectTrigger
          id={id}
          data-fit-select-measured={fitWidth ? "" : undefined}
          {...(filterChrome === "sidebar"
            ? { "data-trade-details-journal-control": "" }
            : filterChrome === "reports"
              ? { "data-reports-filter-select": "" }
              : filterChrome === "card"
                ? { "data-trading-activity-calendar-control": "" }
                : {})}
          className={cn("w-full", filterTriggerClass)}
          style={fitToLongestOption ? fitSelectWidthStyle(fitWidth) : undefined}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent
          className={cn("z-[200]", TRADE_LOG_FILTER_SELECT_CONTENT_CLASS)}
          {...(filterChrome === "sidebar"
            ? { "data-trade-log-filter-select-content": "" }
            : filterChrome === "reports"
              ? { "data-reports-filter-select-content": "" }
              : filterChrome === "card"
                ? { "data-card-filter-select-content": "" }
                : {})}
        >
          <FieldSelectItems
            options={options}
            itemClassName="whitespace-nowrap"
          />
        </SelectContent>
      </Select>
    </div>
  );
}

/** Fixed 10rem filter column — journal open/close style (Trade Log toolbar). */
export function FieldFilterSelect({
  value,
  onValueChange,
  options,
  disabled = false,
  placeholder,
  id,
  emptyValueShowsPlaceholder = false,
  filterChrome = "toolbar",
}: Omit<TradeFilterSelectProps, "layout" | "fitToLongestOption"> & {
  filterChrome?: "toolbar" | "simulator-list";
}): React.ReactElement {
  const selectValue =
    emptyValueShowsPlaceholder && value === "" ? undefined : toRadixSelectValue(value);

  const triggerClass =
    filterChrome === "simulator-list"
      ? SIMULATOR_LIST_FILTER_SELECT_TRIGGER_CLASS
      : TRADE_LOG_FILTER_SELECT_TRIGGER_CLASS;

  return (
    <TradeFilterField>
      <div data-field-select="" className="w-full min-w-0">
        <Select
          value={selectValue}
          onValueChange={(next) => onValueChange(fromRadixSelectValue(next))}
          disabled={disabled}
          className="w-full"
        >
          <SelectTrigger
            id={id}
            data-trade-log-toolbar-field-select=""
            {...(filterChrome === "simulator-list"
              ? { "data-simulator-list-filter-select": "" }
              : {})}
            className={cn("w-full", triggerClass)}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent
            className={cn("z-[200]", TRADE_LOG_FILTER_SELECT_CONTENT_CLASS)}
            data-trade-log-filter-select-content=""
          >
            <FieldSelectItems options={options} />
          </SelectContent>
        </Select>
      </div>
    </TradeFilterField>
  );
}

export const TRADE_DETAILS_UNSET_MOOD_VALUE = "__unset__";

export const TRADE_DETAILS_JOURNAL_NOTES_CLASS =
  "transition-none dark:!border-border dark:!bg-sidebar dark:!text-sidebar-muted-foreground dark:placeholder:!text-sidebar-muted-foreground/70 dark:hover:!bg-sidebar dark:focus-visible:!bg-sidebar";

/** Trade details journal mood selects — full width, Trade Log dark surfaces. */
export function TradeDetailsJournalSelect({
  id,
  value,
  onValueChange,
  options,
  placeholder = "Select mood",
}: {
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: TradeFilterSelectOption[];
  placeholder?: string;
}): React.ReactElement {
  const selectOptions = React.useMemo(
    () => [{ value: TRADE_DETAILS_UNSET_MOOD_VALUE, label: "Not set" }, ...options],
    [options],
  );

  return (
    <div data-field-select="" data-trade-details-journal-select="" className="w-full min-w-0">
      <Select
        value={value || TRADE_DETAILS_UNSET_MOOD_VALUE}
        onValueChange={(next) =>
          onValueChange(next === TRADE_DETAILS_UNSET_MOOD_VALUE ? "" : next)
        }
        className="w-full"
      >
        <SelectTrigger
          id={id}
          data-trade-details-journal-control=""
          className={cn("w-full", TRADE_LOG_FILTER_SELECT_TRIGGER_CLASS)}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent
          className={cn("z-[200]", TRADE_LOG_FILTER_SELECT_CONTENT_CLASS)}
          data-trade-log-filter-select-content=""
        >
          <FieldSelectItems options={selectOptions} />
        </SelectContent>
      </Select>
    </div>
  );
}

export interface TradeMultiFilterSelectProps {
  options: string[];
  selected?: string[];
  onToggle: (value: string) => void;
  emptyLabel: string;
  getTriggerLabel?: (selected: string[]) => string;
  /** `filter` = 10rem column (Trade Log / Reports / Statistics bars). `full` = 100% width. */
  layout?: "filter" | "full";
}

function defaultMultiTriggerLabel(
  selected: string[],
  emptyLabel: string
): string {
  const count = selected.length;
  if (count === 0) return emptyLabel;
  if (count === 1) return selected[0];
  return `${emptyLabel} (${count})`;
}

const MULTI_FILTER_CHECKBOX_ITEM_CLASS = cn(
  TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS,
  TRADE_LOG_FILTER_SELECT_ITEM_CLASS,
  "font-[family-name:var(--font-primary)] font-normal",
  "dark:data-highlighted:!bg-sidebar-accent dark:focus:!bg-sidebar-accent",
);

function TradeMultiFilterSelectMenu({
  options,
  selected,
  onToggle,
  label,
  triggerClassName,
  tradeLogToolbarFieldSelect = false,
  contentClassName,
  wrapClassName,
}: {
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  label: string;
  triggerClassName: string;
  tradeLogToolbarFieldSelect?: boolean;
  contentClassName?: string;
  wrapClassName?: string;
}): React.ReactElement {
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  const menu = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          role="combobox"
          data-slot="select-trigger"
          data-size="default"
          {...(tradeLogToolbarFieldSelect
            ? { "data-trade-log-toolbar-field-select": true as const }
            : {})}
          className={triggerClassName}
        >
          <span
            data-slot="select-value"
            className="min-w-0 flex-1 truncate text-left"
          >
            {label}
          </span>
          <ChevronDownIcon
            className="size-4 shrink-0 opacity-50 dark:opacity-100 dark:text-sidebar-muted-foreground"
            aria-hidden
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        className={cn(
          "z-[200] max-h-56 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto p-1",
          TRADE_LOG_FILTER_SELECT_CONTENT_CLASS,
          contentClassName,
        )}
        data-trade-log-filter-select-content=""
        data-field-select-menu=""
      >
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option}
            checked={selectedSet.has(option)}
            onCheckedChange={() => onToggle(option)}
            onSelect={(event) => event.preventDefault()}
            className={MULTI_FILTER_CHECKBOX_ITEM_CLASS}
          >
            <span className="min-w-0 truncate">{option}</span>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (wrapClassName) {
    return (
      <div data-field-select="" className={wrapClassName}>
        {menu}
      </div>
    );
  }

  return menu;
}

export function TradeMultiFilterSelect({
  options,
  selected = [],
  onToggle,
  emptyLabel,
  getTriggerLabel,
  layout = "filter",
}: TradeMultiFilterSelectProps) {
  const label = getTriggerLabel
    ? getTriggerLabel(selected)
    : defaultMultiTriggerLabel(selected, emptyLabel);

  const menu = (
    <TradeMultiFilterSelectMenu
      options={options}
      selected={selected}
      onToggle={onToggle}
      label={label}
      triggerClassName={cn(
        "inline-flex h-8 w-full min-w-0 items-center justify-between gap-1.5 rounded-[6px] border border-border py-2 pr-2 pl-2.5 text-sm whitespace-nowrap outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
        TRADE_FILTER_SELECT_TRIGGER_CLASS,
        layout === "full" && "w-full",
      )}
    />
  );

  if (layout === "full") {
    return menu;
  }

  return <TradeFilterField>{menu}</TradeFilterField>;
}

/** Fixed 10rem multi-select filter — journal open/close style (Trade Log symbols). */
export function FieldMultiFilterSelect({
  options,
  selected = [],
  onToggle,
  emptyLabel,
  getTriggerLabel,
}: Omit<TradeMultiFilterSelectProps, "layout">): React.ReactElement {
  const label = getTriggerLabel
    ? getTriggerLabel(selected)
    : defaultMultiTriggerLabel(selected, emptyLabel);

  return (
    <TradeFilterField>
      <TradeMultiFilterSelectMenu
        options={options}
        selected={selected}
        onToggle={onToggle}
        label={label}
        triggerClassName={cn("w-full", TRADE_LOG_FILTER_SELECT_TRIGGER_CLASS)}
        tradeLogToolbarFieldSelect
        wrapClassName="w-full min-w-0"
      />
    </TradeFilterField>
  );
}

function filterTradeFilterOptions(
  options: TradeFilterSelectOption[],
  query: string
): TradeFilterSelectOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  const tokens = q.split(/\s+/).filter(Boolean);
  return options.filter((option) => {
    const hay = `${option.label} ${option.value} ${option.keywords ?? ""}`.toLowerCase();
    return tokens.every((token) => hay.includes(token));
  });
}

export type TradeFilterSearchableSelectProps = Omit<
  TradeFilterSelectProps,
  "layout" | "fitToLongestOption" | "emptyValueShowsPlaceholder"
> & {
  /** Accessible name for the combobox (defaults to placeholder / Field). */
  "aria-label"?: string;
  /**
   * How many filtered options to mount initially / per scroll page.
   * Large catalogs (instruments) stay responsive; type-to-search still filters the full list.
   */
  pageSize?: number;
};

const SEARCHABLE_SELECT_DEFAULT_PAGE_SIZE = 50;

/**
 * Searchable full-width field picker (type to filter + click chevron to browse).
 * Used for policy Field dropdowns and Strategy Lab instrument pick.
 * Renders a window of `pageSize` rows and appends more when the list scrolls near the bottom.
 */
export function TradeFilterSearchableSelectFullWidth({
  value,
  onValueChange,
  options,
  disabled = false,
  placeholder = "Search fields…",
  id,
  "aria-label": ariaLabel,
  pageSize = SEARCHABLE_SELECT_DEFAULT_PAGE_SIZE,
}: TradeFilterSearchableSelectProps): React.ReactElement {
  const listboxId = React.useId();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const blurCloseTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const [menuWidth, setMenuWidth] = React.useState<number | undefined>(undefined);
  const [visibleCount, setVisibleCount] = React.useState(pageSize);
  const deferredDraft = React.useDeferredValue(draft);

  const selectedLabel = React.useMemo(() => {
    const match = options.find((option) => option.value === value);
    return match?.label ?? (value ? value : "");
  }, [options, value]);

  const filtered = React.useMemo(() => {
    // While open, filter on the typed query only (empty draft = full list).
    const query = open ? deferredDraft : "";
    return filterTradeFilterOptions(options, query);
  }, [deferredDraft, open, options]);

  const safePageSize = pageSize > 0 ? pageSize : SEARCHABLE_SELECT_DEFAULT_PAGE_SIZE;

  React.useEffect(() => {
    if (!open) return;
    setVisibleCount(safePageSize);
  }, [open, deferredDraft, safePageSize]);

  const visibleOptions = React.useMemo(
    () => filtered.slice(0, Math.max(safePageSize, visibleCount)),
    [filtered, safePageSize, visibleCount]
  );
  const hasMore = visibleOptions.length < filtered.length;

  React.useEffect(() => {
    return () => {
      if (blurCloseTimerRef.current) clearTimeout(blurCloseTimerRef.current);
    };
  }, []);

  const clearBlurClose = () => {
    if (blurCloseTimerRef.current) {
      clearTimeout(blurCloseTimerRef.current);
      blurCloseTimerRef.current = null;
    }
  };

  const syncMenuWidth = () => {
    const width = anchorRef.current?.getBoundingClientRect().width;
    if (width && width > 0) setMenuWidth(Math.round(width));
  };

  const openMenu = () => {
    if (disabled) return;
    clearBlurClose();
    syncMenuWidth();
    setDraft("");
    setVisibleCount(safePageSize);
    setOpen(true);
    // Keep focus on the input — do not steal it into the list.
    requestAnimationFrame(() => {
      syncMenuWidth();
      inputRef.current?.focus({ preventScroll: true });
    });
  };

  const commitOption = (option: TradeFilterSelectOption) => {
    if (option.disabled) return;
    clearBlurClose();
    onValueChange(option.value);
    setDraft(option.label);
    setOpen(false);
  };

  const closeWithoutCommit = () => {
    clearBlurClose();
    setOpen(false);
    setDraft(selectedLabel);
  };

  const isInsideCombobox = (node: EventTarget | null): boolean => {
    if (!(node instanceof Node)) return false;
    if (anchorRef.current?.contains(node)) return true;
    const list = document.getElementById(listboxId);
    return Boolean(list?.contains(node));
  };

  const onListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore) return;
    const el = event.currentTarget;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining > 48) return;
    setVisibleCount((prev) => Math.min(prev + safePageSize, filtered.length));
  };

  return (
    <div data-trade-filter-bar="" data-trade-filter-bar-layout="full" className="w-full min-w-0">
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (disabled) return;
          if (next) {
            openMenu();
            return;
          }
          // Only close via outside dismiss / Escape — not focus moves to the input.
          closeWithoutCommit();
        }}
      >
        <PopoverAnchor asChild>
          <div ref={anchorRef} data-field-select="" className="relative w-full min-w-0">
            <input
              ref={inputRef}
              id={id}
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-label={ariaLabel ?? placeholder}
              disabled={disabled}
              placeholder={placeholder}
              value={open ? draft : selectedLabel}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-1p-ignore=""
              data-lpignore="true"
              data-form-type="other"
              data-slot="select-trigger"
              className={cn(
                "flex h-8 w-full max-w-full items-center rounded-[6px] border border-input bg-transparent py-2 pr-8 pl-2.5 text-sm outline-none",
                "font-[family-name:var(--font-primary)] font-normal normal-case tracking-normal",
                "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
                "disabled:cursor-not-allowed disabled:opacity-50",
                "dark:bg-input/30 dark:hover:bg-input/50",
                "placeholder:text-muted-foreground",
                FIELD_SELECT_TRIGGER_CLASS,
              )}
              onFocus={() => {
                clearBlurClose();
                if (!open) openMenu();
              }}
              onClick={() => {
                clearBlurClose();
                if (!open) openMenu();
              }}
              onChange={(event) => {
                clearBlurClose();
                setDraft(event.target.value);
                if (!open) {
                  syncMenuWidth();
                  setVisibleCount(safePageSize);
                  setOpen(true);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  closeWithoutCommit();
                  inputRef.current?.blur();
                  return;
                }
                if (event.key === "Enter") {
                  event.preventDefault();
                  const first = visibleOptions.find((option) => !option.disabled);
                  if (first) commitOption(first);
                  return;
                }
                if (event.key === "ArrowDown" && !open) {
                  event.preventDefault();
                  openMenu();
                }
              }}
              onBlur={(event) => {
                const next = event.relatedTarget;
                if (isInsideCombobox(next)) {
                  clearBlurClose();
                  return;
                }
                blurCloseTimerRef.current = setTimeout(() => {
                  const active = document.activeElement;
                  if (isInsideCombobox(active)) return;
                  setOpen(false);
                  setDraft(selectedLabel);
                }, 150);
              }}
            />
            <button
              type="button"
              tabIndex={-1}
              aria-label={open ? "Close options" : "Open options"}
              disabled={disabled}
              className={cn(
                "absolute top-1/2 right-1 flex size-6 -translate-y-1/2 items-center justify-center rounded-[4px]",
                "text-gray-500 opacity-70 hover:bg-muted hover:opacity-100",
                "disabled:pointer-events-none disabled:opacity-40",
                "dark:text-sidebar-muted-foreground",
              )}
              onMouseDown={(event) => {
                event.preventDefault();
                clearBlurClose();
                if (open) {
                  closeWithoutCommit();
                  return;
                }
                openMenu();
              }}
            >
              <ChevronDownIcon aria-hidden className="size-4" />
            </button>
          </div>
        </PopoverAnchor>
        <PopoverContent
          id={listboxId}
          role="listbox"
          align="start"
          side="bottom"
          sideOffset={4}
          avoidCollisions={false}
          className={cn(
            "z-[200] max-h-72 overflow-y-auto p-1",
            TRADE_LOG_FILTER_SELECT_CONTENT_CLASS,
          )}
          style={menuWidth ? { width: menuWidth } : undefined}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onFocusOutside={(event) => {
            // Input lives in the anchor, outside Content — don't dismiss for that.
            if (isInsideCombobox(event.target)) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (isInsideCombobox(event.target)) {
              event.preventDefault();
            }
          }}
          onInteractOutside={(event) => {
            if (isInsideCombobox(event.target)) {
              event.preventDefault();
            }
          }}
          onScroll={onListScroll}
        >
          {filtered.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">No matches</p>
          ) : (
            <>
              {visibleOptions.map((option, index) => {
                const selected = option.value === value;
                return (
                  <button
                    key={`${option.value}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    disabled={option.disabled}
                    className={cn(
                      "flex w-full cursor-default items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-sm outline-none",
                      TRADE_LOG_FILTER_SELECT_ITEM_CLASS,
                      "font-[family-name:var(--font-primary)] font-normal",
                      "hover:bg-accent dark:hover:bg-sidebar-accent",
                      "disabled:cursor-not-allowed disabled:opacity-45",
                      selected && "bg-accent/60 dark:bg-sidebar-accent",
                    )}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      commitOption(option);
                    }}
                  >
                    <CheckIcon
                      className={cn("size-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  </button>
                );
              })}
              {hasMore ? (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">
                  Showing {visibleOptions.length} of {filtered.length}. Scroll for more.
                  {deferredDraft.trim() ? "" : ", or type to search"}
                </p>
              ) : null}
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
